import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/auth";
import { useBrandName } from "@/lib/api";
import { useRoleLabel } from "@/lib/roles";
import { setAdminLocale } from "@/i18n";
import {
  CalendarIcon,
  ChartIcon,
  ClockIcon,
  HomeIcon,
  LeadsIcon,
  PersonIcon,
  PhoneIcon,
  PluginIcon,
  ShieldIcon,
  SparkIcon,
} from "@/components/Icons";

/**
 * Platform super-admin chrome. Distinct from the operator AppShell in
 * three ways: (1) amber accent rail instead of green so the chrome reads
 * as "platform admin, not tenant operator"; (2) "Platform Admin" brand
 * block with shield icon instead of a tenant slug; (3) desktop-first
 * layout — no bottom-nav, sidebar is always visible because the admin
 * audience is at a desk, not on a phone.
 *
 * Phase 12 i18n: every label runs through useTranslation(). The locale
 * switcher in the header writes to localStorage["admin.locale"] via
 * {@link setAdminLocale}; super-admins have no per-row preference (no
 * tenant_id, no app_user) so localStorage is the only reasonable store
 * without a schema migration nobody's asked for.
 */
export default function AdminShell() {
  const { user, logout } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const location = useLocation();

  if (!user) return null;

  const initials = (() => {
    const fn = user.full_name?.trim();
    if (fn && fn.length > 0) {
      const parts = fn.split(/\s+/);
      const a = parts[0]?.[0] ?? "";
      const b = parts.length > 1 ? parts[parts.length - 1][0] : "";
      return (a + b).toUpperCase() || user.email.slice(0, 2).toUpperCase();
    }
    return user.email.slice(0, 2).toUpperCase();
  })();

  async function onLogout() {
    setSigningOut(true);
    try {
      await logout();
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header
        email={user.email}
        role={user.role}
        initials={initials}
        signingOut={signingOut}
        onLogout={onLogout}
        currentPath={location.pathname}
      />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

interface HeaderProps {
  email: string;
  role: string;
  initials: string;
  signingOut: boolean;
  onLogout: () => void;
  currentPath: string;
}

function Header({ email, role, initials, signingOut, onLogout, currentPath }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const roleLabel = useRoleLabel();
  const brandName = useBrandName();
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur">
      <div className="flex h-14 items-center gap-4 px-4 md:px-6">
        <NavLink to="/" className="flex items-center gap-2.5 group">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-accent/15 ring-1 ring-accent/25 text-accent">
            <ShieldIcon size={16} />
          </span>
          <span className="leading-tight">
            <span className="block text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t("nav.platformBadge")}
            </span>
            <span className="block text-sm font-semibold">
              {brandName ? `${brandName} · admin` : "Admin"}
            </span>
          </span>
        </NavLink>
        <span className="ml-2 hidden md:inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent">
          <SparkIcon size={10} />
          {t("nav.superAdminBadge")}
        </span>

        <div className="ml-auto flex items-center gap-2">
          <LocaleSwitcher current={i18n.language} />
          <div className="flex items-center gap-1 rounded-md border border-border bg-card px-1 py-1">
            <NavLink
              to="/account"
              title={t("nav.account")}
              className="flex items-center gap-2 rounded px-1.5 py-0.5 hover:bg-muted"
            >
              <span className="grid h-6 w-6 place-items-center rounded-full bg-accent/15 text-[11px] font-semibold text-accent">
                {initials}
              </span>
              <span className="hidden md:inline text-xs leading-tight text-left">
                <span className="block text-foreground font-medium">{email}</span>
                <span className="block text-muted-foreground -mt-0.5">{roleLabel(role)}</span>
              </span>
            </NavLink>
            <button
              type="button"
              onClick={onLogout}
              disabled={signingOut}
              data-current-path={currentPath}
              className="ml-0.5 rounded px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              {signingOut ? "…" : t("nav.logout")}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

/**
 * Two-button locale toggle in the header. Stores the choice in
 * localStorage so it survives page refresh and tab restarts. No backend
 * roundtrip because super_admin has no preferred-locale column.
 */
function LocaleSwitcher({ current }: { current: string }) {
  const { t } = useTranslation();
  const active = current.startsWith("en") ? "en" : "sv";
  function set(lang: "sv" | "en") {
    if (lang === active) return;
    setAdminLocale(lang);
  }
  return (
    <div
      className="hidden md:inline-flex items-center rounded-md border border-border bg-card text-[11px]"
      role="group"
      aria-label={t("language.label")}
    >
      <button
        type="button"
        onClick={() => set("sv")}
        className={`px-2 py-1 ${
          active === "sv" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
        }`}
        aria-pressed={active === "sv"}
        title={t("language.swedish")}
      >
        SV
      </button>
      <button
        type="button"
        onClick={() => set("en")}
        className={`px-2 py-1 ${
          active === "en" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
        }`}
        aria-pressed={active === "en"}
        title={t("language.english")}
      >
        EN
      </button>
    </div>
  );
}

interface NavEntry {
  to: string;
  /** i18n key under `nav.*` — translate at render time. */
  labelKey: string;
  Icon: (p: { size?: number | string }) => JSX.Element;
}

const NAV: NavEntry[] = [
  { to: "/", labelKey: "nav.dashboard", Icon: HomeIcon },
  { to: "/tenants", labelKey: "nav.tenants", Icon: PersonIcon },
  { to: "/leads", labelKey: "nav.leads", Icon: LeadsIcon },
  { to: "/prompt-modules", labelKey: "nav.promptModules", Icon: SparkIcon },
  { to: "/dispatcher", labelKey: "nav.dispatcher", Icon: ClockIcon },
  { to: "/audit-log", labelKey: "nav.auditLog", Icon: ChartIcon },
  { to: "/health", labelKey: "nav.health", Icon: SparkIcon },
  { to: "/integrations", labelKey: "nav.integrations", Icon: PluginIcon },
  { to: "/sms-numbers", labelKey: "nav.smsNumbers", Icon: PhoneIcon },
  { to: "/super-admins", labelKey: "nav.superAdmins", Icon: ShieldIcon },
];

function Sidebar() {
  return (
    <aside className="hidden md:block w-56 shrink-0 border-r border-border min-h-[calc(100vh-3.5rem)]">
      <nav className="sticky top-14 flex flex-col gap-0.5 p-3">
        {NAV.map((entry) => (
          <NavItem key={entry.to} {...entry} />
        ))}
        <div className="my-2 h-px bg-border" />
        <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Calendar
        </p>
        <NavItem to="/integrations" labelKey="nav.googleOauth" Icon={CalendarIcon} />
      </nav>
    </aside>
  );
}

function NavItem({ to, labelKey, Icon }: NavEntry) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        [
          "relative flex items-center gap-3 rounded-md px-3 py-2 text-sm",
          isActive
            ? "bg-card text-foreground before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:bg-accent before:rounded-r-full"
            : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
        ].join(" ")
      }
    >
      <Icon size={16} />
      <span className="truncate">{t(labelKey)}</span>
    </NavLink>
  );
}
